// Story 10.2 — Empty state del dashboard. Sin formulario "crear desde web"
// (el wizard vive en el CLI por ahora); mostramos el comando y un copy button.
// Story 13.1 — añadido botón "Crear bot" que abre el BotCreationWizard.

import { Bot, Copy, Plus } from 'lucide-react';
import { useState } from 'react';
import { Can } from '@/components/auth/Can';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BotCreationWizard } from './BotCreationWizard';

const CLI_COMMAND = 'revel-bots init';

export function EmptyState(): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  async function handleCopy(): Promise<void> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(CLI_COMMAND);
      } else {
        // Fallback para browsers sin permission API.
        const ta = document.createElement('textarea');
        ta.value = CLI_COMMAND;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fail silently — el comando es trivial de copiar a mano.
    }
  }

  return (
    <>
      <Card className="mx-auto max-w-2xl">
        <CardHeader className="items-center text-center">
          <div className="rounded-full bg-primary/10 p-4">
            <Bot className="h-8 w-8 text-primary" aria-hidden />
          </div>
          <CardTitle>Aún no tienes bots</CardTitle>
          <CardDescription>
            Crea tu primer bot desde el dashboard, o usa la CLI si prefieres terminal.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* M-9 audit: la creación de bots requiere `edit_bot`. Igual que el
              botón "+ Crear bot" del header del dashboard, este affordance se
              oculta para roles sin la capability (e.g. support). */}
          <Can capability="edit_bot">
            <div className="flex justify-center">
              <Button onClick={() => setWizardOpen(true)} variant="default" size="lg">
                <Plus className="h-4 w-4" />
                Crear bot
              </Button>
            </div>
          </Can>
          <div className="border-t pt-4">
            <p className="mb-2 text-center text-xs text-muted-foreground">
              ¿Prefieres la línea de comandos?
            </p>
            <pre className="overflow-x-auto rounded-md border border-border bg-muted/50 p-3 font-mono text-sm">
              <code>{CLI_COMMAND}</code>
            </pre>
            <div className="mt-2 flex justify-center gap-2">
              <Button onClick={handleCopy} variant="outline" size="sm">
                <Copy className="h-4 w-4" />
                {copied ? 'Copiado' : 'Copiar comando'}
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <a href="https://docs.revelation-bots.dev" target="_blank" rel="noreferrer">
                  Ver documentación
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <BotCreationWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </>
  );
}
